import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { netconWarteschlangenQuery, netconZugangQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * NetCon-Zugang: Zugangsdaten hinterlegen und Warteschlangen wählen (AP 3.1).
 *
 * Von Nils gewünscht: „Können wir die NetCon API Zugangsdaten sonst übers Frontend
 * verschlüsselt speichern?" und „Die zu ladende Warteschlange würde ich gerne in den
 * Settings einstellen."
 *
 * ## Nur schreibend – und was das für die Bedienung heißt
 *
 * Das Passwortfeld ist **immer leer**, auch wenn Zugangsdaten hinterlegt sind. Es
 * wird nicht mit Sternchen vorbelegt: eine Maske mit der richtigen Anzahl Zeichen
 * sieht wie ein gespeicherter Wert aus, den man bearbeiten könnte – und verrät die
 * Länge. Angezeigt wird stattdessen der **Benutzername** und das Ergebnis des
 * letzten Verbindungstests. Der Benutzername ist nicht das Geheimnis, aber er ist
 * die erste Auskunft, wenn eine Anmeldung scheitert: falsches Konto sieht sonst
 * genauso aus wie falsches Passwort.
 *
 * ## Warum das Prüfergebnis gespeichert ist und nicht nur angezeigt
 *
 * Nach einem Neuladen stünde dort sonst wieder nichts, und „geprüft" wäre eine
 * Auskunft, die nur die Person hat, die gerade geklickt hat. Nach einem Fehlschlag
 * ist genau das die Frage: hat es jemals getragen, und seit wann nicht mehr?
 */
export function NetConZugang() {
  const { data: zugang, error } = useQuery(netconZugangQuery)
  const [benutzer, setBenutzer] = useState('')
  const [passwort, setPasswort] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const queryClient = useQueryClient()

  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['netcon'] })

  const uebernehmen = (r: { ok: boolean; meldung: string }) => {
    setMeldung({ art: r.ok ? 'ok' : 'fehler', text: r.meldung })
    /** Passwort aus dem Formularzustand entfernen, sobald es übergeben ist. */
    setPasswort('')
    neuLaden()
  }

  const speichern = useMutation({
    mutationFn: (v: { benutzer: string; passwort: string; baseUrl?: string }) =>
      trpc.netcon.zugangSetzen.mutate(v),
    onSuccess: uebernehmen,
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const pruefen = useMutation({
    mutationFn: () => trpc.netcon.verbindungPruefen.mutate(),
    onSuccess: uebernehmen,
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const entfernen = useMutation({
    mutationFn: () => trpc.netcon.zugangEntfernen.mutate(),
    onSuccess: () => {
      setMeldung({ art: 'ok', text: 'Zugangsdaten entfernt.' })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /**
   * Die Abfrage ist auf `admin` beschränkt. Für alle anderen gibt es diese Karte
   * gar nicht – dieselbe Behandlung wie bei der Intranet-Zuordnung, statt einer
   * Fehlermeldung an einer Stelle, die sie nichts angeht.
   */
  if (error) return null

  const laeuft = speichern.isPending || pruefen.isPending
  const kannSpeichern = benutzer.trim().length > 0 && passwort.length > 0 && !laeuft

  return (
    <div className="grid gap-3">
      <Card>
        <CardPad>
          <div className="flex flex-wrap items-baseline gap-2">
            <b className="text-[13px]">NetCon-Zugangsdaten</b>
            <span className="text-[11.5px] text-muted">
              verschlüsselt gespeichert · nach dem Speichern nicht mehr auslesbar
            </span>
            <span className="ml-auto">
              <Zustand zugang={zugang} />
            </span>
          </div>

          {zugang?.gesetzt && !zugang.schluesselVorhanden && (
            <Hinweis art="fehler">
              Es sind Zugangsdaten hinterlegt, aber <code>CREDENTIAL_KEY</code> fehlt in der{' '}
              <code>.env</code> auf dem Server. Ohne den Schlüssel sind sie nicht lesbar – der
              Abgleich kann sich nicht anmelden.
            </Hinweis>
          )}

          {zugang?.gesetzt && (
            <div className="mt-2.5 text-[12.5px]">
              <span className="text-ink2">Benutzer</span>{' '}
              <strong className="font-mono">{zugang.benutzer}</strong>
              {zugang.letztePruefungAm && (
                <span className="ml-3 text-muted">
                  {zugang.letztePruefungOk ? '✓ ' : '✗ '}
                  {zugang.letztePruefungOk
                    ? (zugang.letztePruefungInfo ?? 'Verbindung stand')
                    : 'Verbindungstest fehlgeschlagen'}{' '}
                  ({new Date(zugang.letztePruefungAm).toLocaleString('de-DE')})
                </span>
              )}
              {!zugang.letztePruefungAm && (
                <span className="ml-3 text-muted">noch nie geprüft</span>
              )}
            </div>
          )}

          {zugang?.letzterFehler && !zugang.letztePruefungOk && (
            <div className="mt-1.5 text-[11.5px] text-pool-sofort">{zugang.letzterFehler}</div>
          )}

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <label className="grid gap-1 text-[11.5px] font-medium text-ink2">
              Benutzername
              <input
                type="text"
                autoComplete="off"
                value={benutzer}
                onChange={(e) => setBenutzer(e.target.value)}
                placeholder={zugang?.benutzer ?? 'Dienstkonto'}
                className="rounded-lg border border-line2 px-2.5 py-1.5 text-[13px]"
              />
            </label>
            <label className="grid gap-1 text-[11.5px] font-medium text-ink2">
              Passwort
              <input
                type="password"
                autoComplete="new-password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                /**
                 * Bewusst kein Platzhalter mit Sternchen: der sähe wie ein
                 * gespeicherter Wert aus. Dass etwas hinterlegt ist, steht oben.
                 */
                placeholder={zugang?.gesetzt ? 'neues Passwort eingeben' : ''}
                className="rounded-lg border border-line2 px-2.5 py-1.5 text-[13px]"
              />
            </label>
          </div>

          <label className="mt-2 grid gap-1 text-[11.5px] font-medium text-ink2">
            Basis-Adresse der API
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={zugang?.config.baseUrl ?? 'https://api.netcon.cloud'}
              className="rounded-lg border border-line2 px-2.5 py-1.5 font-mono text-[12px]"
            />
          </label>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!kannSpeichern}
              onClick={() =>
                speichern.mutate({
                  benutzer: benutzer.trim(),
                  passwort,
                  ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
                })
              }
            >
              {speichern.isPending ? 'Speichere …' : 'Speichern und prüfen'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!zugang?.gesetzt || laeuft}
              onClick={() => pruefen.mutate()}
            >
              {pruefen.isPending ? 'Prüfe …' : 'Verbindung prüfen'}
            </Button>
            {zugang?.gesetzt && (
              <Button
                variant="ghost"
                size="sm"
                disabled={entfernen.isPending}
                onClick={() => entfernen.mutate()}
                className="ml-auto text-pool-sofort"
              >
                Zugangsdaten entfernen
              </Button>
            )}
          </div>

          {meldung && (
            <Hinweis art={meldung.art === 'ok' ? 'ok' : 'fehler'}>{meldung.text}</Hinweis>
          )}
        </CardPad>
      </Card>

      <Warteschlangen />
    </div>
  )
}

/**
 * Welche Warteschlangen geladen werden.
 *
 * Die Liste kommt live aus `queue/list/v2` – abgetippt wäre jede Kennung ein
 * möglicher Zahlendreher, und ein falsch angekreuzter Filter fällt erst auf, wenn
 * Tickets fehlen (oder fremde erscheinen).
 */
function Warteschlangen() {
  const { data, error } = useQuery(netconWarteschlangenQuery)
  const [wahl, setWahl] = useState<number[] | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const setzen = useMutation({
    mutationFn: (queues: number[]) => trpc.netcon.warteschlangenSetzen.mutate({ queues }),
    onSuccess: (r) => {
      setMeldung(
        r.queues.length === 0
          ? 'Keine Warteschlange gewählt – es wird nichts importiert.'
          : `${r.queues.length} Warteschlangen gespeichert.`,
      )
      setWahl(null)
      void queryClient.invalidateQueries({ queryKey: ['netcon'] })
    },
    onError: (e: Error) => setMeldung(e.message),
  })

  if (error) return null

  const gewaehlt = wahl ?? data?.gewaehlt ?? []
  const geaendert =
    wahl !== null && [...gewaehlt].sort().join() !== [...(data?.gewaehlt ?? [])].sort().join()

  const umschalten = (id: number) =>
    setWahl(gewaehlt.includes(id) ? gewaehlt.filter((q) => q !== id) : [...gewaehlt, id])

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[13px]">Warteschlangen</b>
          <span className="text-[11.5px] text-muted">
            was davon importiert wird · leer heißt: nichts
          </span>
          <span className="ml-auto text-[11.5px] text-muted">
            {gewaehlt.length} von {data?.warteschlangen.length ?? 0} gewählt
          </span>
        </div>

        {data?.quellFehler && (
          <Hinweis art="fehler">
            Die Liste ließ sich nicht holen: {data.quellFehler}
            {gewaehlt.length > 0 && (
              <> Gespeichert ist trotzdem: {gewaehlt.join(', ')} – die Auswahl bleibt bestehen.</>
            )}
          </Hinweis>
        )}

        {data && data.warteschlangen.length > 0 && (
          <div className="mt-2.5 grid gap-1">
            {data.warteschlangen.map((w) => (
              <label
                key={w.id}
                className="flex cursor-pointer items-baseline gap-2 rounded-lg px-1.5 py-1 text-[12.5px] hover:bg-[#f7f6f3]"
              >
                <input
                  type="checkbox"
                  checked={gewaehlt.includes(w.id)}
                  onChange={() => umschalten(w.id)}
                />
                <span className="font-medium">{w.name}</span>
                <span className="font-mono text-[11px] text-muted">#{w.id}</span>
                {w.beschreibung && (
                  <span className="truncate text-[11.5px] text-muted">{w.beschreibung}</span>
                )}
              </label>
            ))}
          </div>
        )}

        {geaendert && (
          <div className="mt-2.5 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={setzen.isPending}
              onClick={() => setzen.mutate(gewaehlt)}
            >
              Auswahl speichern
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setWahl(null)}>
              Verwerfen
            </Button>
          </div>
        )}

        {meldung && <Hinweis art="ok">{meldung}</Hinweis>}

        <p className="mt-2.5 text-[11.5px] text-muted">
          Der Import selbst entsteht in AP 3.3. Bis dahin ist diese Auswahl die Vorbereitung – es
          wird noch nichts geholt.
        </p>
      </CardPad>
    </Card>
  )
}

/** „gesetzt / nicht gesetzt" – die einzige Aussage über das Geheimnis, die es gibt. */
function Zustand({
  zugang,
}: {
  zugang: { gesetzt: boolean; schluesselVorhanden: boolean } | undefined
}) {
  if (!zugang) return null
  const gut = zugang.gesetzt && zugang.schluesselVorhanden
  return (
    <span
      className={`inline-flex items-center rounded-full px-[9px] py-0.5 text-[10.5px] font-bold ${
        gut ? 'bg-[#e5f7f0] text-[#0d7a52]' : 'bg-[#f0efec] text-[#6d6b65]'
      }`}
    >
      {zugang.gesetzt ? 'gesetzt' : 'nicht gesetzt'}
    </span>
  )
}

function Hinweis({ art, children }: { art: 'ok' | 'fehler'; children: React.ReactNode }) {
  return (
    <div
      className={`mt-2.5 rounded-lg px-2.5 py-2 text-[12px] ${
        art === 'ok' ? 'bg-[#e5f7f0] text-[#0d7a52]' : 'bg-[#fdeaea] text-[#b02a2a]'
      }`}
      role={art === 'fehler' ? 'alert' : 'status'}
    >
      {children}
    </div>
  )
}
